import * as Sentry from "@sentry/react";

import {
  getGraphQlOperationName,
  isExpectedGraphQlError,
  isGraphQlErrorAlreadyCaptured,
} from "./api/graphql/graphQlErrorStatus";
import { getConfig } from "./config";

const APP_ENV = getConfig("VITE_APP_ENV");
const SENTRY_DSN = getConfig("VITE_APP_SENTRY_DSN");
const SENTRY_RELEASE = getConfig("VITE_APP_SENTRY_RELEASE");

const isEnabled = SENTRY_DSN && ["production", "staging"].includes(APP_ENV);

const envTracesSampleRateMap = new Map<string, number>([
  ["production", 0.5],
  ["staging", 1],
  ["development", 1],
]);

/**
 * Single choke point for every captured event: an expected 4xx GraphQL
 * failure is not a fault, a failure the Sentry link already captured is a
 * duplicate, and a real one is grouped by its operation.
 */
export const beforeSend = (
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
): Sentry.ErrorEvent | null => {
  if (
    isExpectedGraphQlError(hint.originalException) ||
    isGraphQlErrorAlreadyCaptured(hint.originalException)
  ) {
    return null;
  }

  const operationName = getGraphQlOperationName(hint.originalException);
  if (!operationName) {
    return event;
  }

  // Sentry orders the primary exception last; linked causes come first
  const exception = event.exception?.values?.at(-1);
  if (exception) {
    exception.value = `[${operationName}] ${exception.value ?? ""}`.trim();
  }
  event.tags = { ...event.tags, graphqlOperation: operationName };
  event.fingerprint = ["{{ default }}", operationName];
  return event;
};

if (isEnabled) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: APP_ENV,
    release: SENTRY_RELEASE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: envTracesSampleRateMap.get(APP_ENV),
    beforeSend,
  });
}

export const reportError = isEnabled ? Sentry.captureException : () => null;
export const setSentryUser = isEnabled ? Sentry.setUser : () => null;
